import React, { useState, useRef, useEffect } from 'react';
import './Chatbot.css';
import { GoogleGenAI } from "@google/genai";
import { SERVICES_PRICING, AVAILABLE_SLOTS } from '../constants';
import * as CalendarService from '../services/mockCalendarService';

const CalinChatbot = ({ isOpen, onClose }) => {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  const ai = apiKey ? new GoogleGenAI({ apiKey }) : null;
  
  // Log API key status (for debugging - remove in production)
  useEffect(() => {
    if (!apiKey) {
      console.warn('CalinChatbot: REACT_APP_GEMINI_API_KEY is not set');
    } else {
      console.log('CalinChatbot: API key is configured');
    }
  }, [apiKey]);

  const [messages, setMessages] = useState([
    {
      id: 1,
      text: apiKey 
        ? "Hello! 👋 I'm Calin, your AI receptionist. I can help you with:\n• Scheduling appointments\n• Checking availability\n• Service information and pricing\n• Answering your questions\n\nHow can I assist you today?"
        : "Hello! 👋 I'm Calin, your AI receptionist. However, I need an API key to be fully functional. Please set REACT_APP_GEMINI_API_KEY in your .env file.\n\nFor now, I can still help you understand my capabilities!",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const quickReplies = [
    "Schedule Appointment",
    "Check Availability",
    "Service Pricing",
    "Contact Information"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    if (!ai) {
      const errorMessage = {
        id: messages.length + 2,
        text: "I'm Calin, but I need to be configured with an API key to respond. Please contact support to get me fully operational! 😊",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
      return;
    }

    try {
      // Check if user is asking about availability or wants to book
      const lowerMessage = inputMessage.toLowerCase();
      let availabilityInfo = '';
      let pricingInfo = JSON.stringify(SERVICES_PRICING, null, 2);
      
      // Extract date if mentioned
      const dateMatch = inputMessage.match(/\d{4}-\d{2}-\d{2}|today|tomorrow|next\s+\w+/i);
      if (dateMatch || lowerMessage.includes('available') || lowerMessage.includes('availability')) {
        const checkDate = dateMatch ? dateMatch[0] : new Date().toISOString().split('T')[0];
        const available = CalendarService.checkAvailability(checkDate);
        availabilityInfo = `Available slots for ${checkDate}: ${available.length > 0 ? available.join(', ') : 'No slots available'}`;
      }

      const systemPrompt = `You are Calin, a professional and friendly AI receptionist for a business. Your role is to help customers with:

1. Scheduling and managing appointments
2. Answering questions about services and pricing
3. Providing business information
4. Handling customer inquiries professionally

PERSONALITY:
- Warm, professional, and helpful
- Concise but friendly (2-3 sentences max per response)
- Use natural, conversational language
- Be empathetic and solution-oriented

BUSINESS INFORMATION:
- Service Pricing: ${pricingInfo}
- Available Time Slots: ${AVAILABLE_SLOTS.join(', ')}
- Business Hours: 9 AM to 5 PM, Monday to Friday
- Today's Date: ${new Date().toLocaleDateString()}
${availabilityInfo ? `- ${availabilityInfo}` : ''}

CAPABILITIES:
- Check appointment availability (use the availability info provided)
- Schedule appointments (ask for: name, date, time, reason/type, phone number)
- Provide service information and pricing (use the pricing info provided)
- Answer FAQs about the business
- Handle appointment cancellations and rescheduling

IMPORTANT:
- When scheduling, you MUST collect: patient name, date (YYYY-MM-DD format), time, appointment type/reason, and phone number
- Always confirm appointment details before finalizing
- Use the actual pricing and availability information provided above
- Be specific about available times and prices

CONVERSATION HISTORY:
${messages.map(msg => `${msg.sender === 'user' ? 'Customer' : 'Calin'}: ${msg.text}`).join('\n')}

Customer: ${inputMessage}
Calin:`;

      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash",
        contents: systemPrompt
      });

      // Handle response - check different possible response structures
      let botResponse;
      if (response?.response?.text) {
        botResponse = response.response.text;
      } else if (response?.text) {
        botResponse = response.text;
      } else if (typeof response === 'string') {
        botResponse = response;
      } else {
        console.error('Unexpected response format:', response);
        throw new Error('Unexpected response format from API');
      }
      
      // Check if the response indicates a booking was made
      const bookingKeywords = ['booked', 'scheduled', 'appointment confirmed', 'successfully booked'];
      const isBooking = bookingKeywords.some(keyword => botResponse.toLowerCase().includes(keyword));
      
      // Try to extract booking details from conversation context
      if (isBooking) {
        const allMessages = messages.map(m => m.text).join(' ') + ' ' + inputMessage;
        const dateMatch = allMessages.match(/\d{4}-\d{2}-\d{2}/);
        const timeMatch = allMessages.match(/\d{1,2}:\d{2}/);
        const nameMatch = allMessages.match(/(?:name is|i'm|i am|call me|my name)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i);
        
        if (dateMatch && timeMatch) {
          const date = dateMatch[0];
          let time = timeMatch[0];
          if (time.length === 4) time = '0' + time; // Ensure HH:mm format
          const name = nameMatch ? nameMatch[1] : 'Customer';
          const phoneMatch = allMessages.match(/(\d{3}[-.]?\d{3}[-.]?\d{4})/);
          const phone = phoneMatch ? phoneMatch[1] : '000-000-0000';
          const typeMatch = allMessages.match(/(checkup|cleaning|emergency|consultation|x-ray|root canal|whitening)/i);
          const type = typeMatch ? typeMatch[1] : 'Consultation';
          
          // Actually book the appointment
          const booked = CalendarService.bookAppointment(date, time, name, type, phone);
          if (booked) {
            botResponse += `\n\n✅ Appointment confirmed in our system!`;
          } else {
            botResponse += `\n\n⚠️ That time slot may already be taken. Let me check other available times.`;
          }
        }
      }

      const botMessage = {
        id: messages.length + 2,
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error details:', error.message, error.stack);
      
      let errorText = "I apologize, but I'm having trouble processing that right now. ";
      if (error.message?.includes('API key')) {
        errorText = "I'm Calin, but I need to be configured with an API key to respond. Please check your environment variables.";
      } else if (error.message?.includes('model')) {
        errorText = "There's an issue with the AI model. Please try again in a moment.";
      } else {
        errorText += "Could you please try rephrasing your question?";
      }
      
      const errorMessage = {
        id: messages.length + 2,
        text: errorText,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickReply = async (reply) => {
    if (isLoading) return;

    const userMessage = {
      id: messages.length + 1,
      text: reply,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    if (!ai) {
      const errorMessage = {
        id: messages.length + 2,
        text: "I'm Calin, but I need to be configured with an API key to respond. Please contact support to get me fully operational! 😊",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
      return;
    }

    try {
      // Check if user is asking about availability or wants to book
      const lowerReply = reply.toLowerCase();
      let availabilityInfo = '';
      let pricingInfo = JSON.stringify(SERVICES_PRICING, null, 2);
      
      // Extract date if mentioned
      const dateMatch = reply.match(/\d{4}-\d{2}-\d{2}|today|tomorrow|next\s+\w+/i);
      if (dateMatch || lowerReply.includes('available') || lowerReply.includes('availability')) {
        const checkDate = dateMatch ? dateMatch[0] : new Date().toISOString().split('T')[0];
        const available = CalendarService.checkAvailability(checkDate);
        availabilityInfo = `Available slots for ${checkDate}: ${available.length > 0 ? available.join(', ') : 'No slots available'}`;
      }

      const systemPrompt = `You are Calin, a professional and friendly AI receptionist for a business. Your role is to help customers with:

1. Scheduling and managing appointments
2. Answering questions about services and pricing
3. Providing business information
4. Handling customer inquiries professionally

PERSONALITY:
- Warm, professional, and helpful
- Concise but friendly (2-3 sentences max per response)
- Use natural, conversational language
- Be empathetic and solution-oriented

BUSINESS INFORMATION:
- Service Pricing: ${pricingInfo}
- Available Time Slots: ${AVAILABLE_SLOTS.join(', ')}
- Business Hours: 9 AM to 5 PM, Monday to Friday
- Today's Date: ${new Date().toLocaleDateString()}
${availabilityInfo ? `- ${availabilityInfo}` : ''}

CAPABILITIES:
- Check appointment availability (use the availability info provided)
- Schedule appointments (ask for: name, date, time, reason/type, phone number)
- Provide service information and pricing (use the pricing info provided)
- Answer FAQs about the business
- Handle appointment cancellations and rescheduling

IMPORTANT:
- When scheduling, you MUST collect: patient name, date (YYYY-MM-DD format), time, appointment type/reason, and phone number
- Always confirm appointment details before finalizing
- Use the actual pricing and availability information provided above
- Be specific about available times and prices

CONVERSATION HISTORY:
${messages.map(msg => `${msg.sender === 'user' ? 'Customer' : 'Calin'}: ${msg.text}`).join('\n')}

Customer: ${reply}
Calin:`;

      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash",
        contents: systemPrompt
      });

      // Handle response - check different possible response structures
      let botResponse;
      if (response?.response?.text) {
        botResponse = response.response.text;
      } else if (response?.text) {
        botResponse = response.text;
      } else if (typeof response === 'string') {
        botResponse = response;
      } else {
        console.error('Unexpected response format:', response);
        throw new Error('Unexpected response format from API');
      }
      
      // Check if the response indicates a booking was made
      const bookingKeywords = ['booked', 'scheduled', 'appointment confirmed', 'successfully booked'];
      const isBooking = bookingKeywords.some(keyword => botResponse.toLowerCase().includes(keyword));
      
      // Try to extract booking details from conversation context
      if (isBooking) {
        const allMessages = messages.map(m => m.text).join(' ') + ' ' + reply;
        const dateMatch = allMessages.match(/\d{4}-\d{2}-\d{2}/);
        const timeMatch = allMessages.match(/\d{1,2}:\d{2}/);
        const nameMatch = allMessages.match(/(?:name is|i'm|i am|call me|my name)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i);
        
        if (dateMatch && timeMatch) {
          const date = dateMatch[0];
          let time = timeMatch[0];
          if (time.length === 4) time = '0' + time;
          const name = nameMatch ? nameMatch[1] : 'Customer';
          const phoneMatch = allMessages.match(/(\d{3}[-.]?\d{3}[-.]?\d{4})/);
          const phone = phoneMatch ? phoneMatch[1] : '000-000-0000';
          const typeMatch = allMessages.match(/(checkup|cleaning|emergency|consultation|x-ray|root canal|whitening)/i);
          const type = typeMatch ? typeMatch[1] : 'Consultation';
          
          // Actually book the appointment
          const booked = CalendarService.bookAppointment(date, time, name, type, phone);
          if (booked) {
            botResponse += `\n\n✅ Appointment confirmed in our system!`;
          } else {
            botResponse += `\n\n⚠️ That time slot may already be taken. Let me check other available times.`;
          }
        }
      }

      const botMessage = {
        id: messages.length + 2,
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error details:', error.message, error.stack);
      
      let errorText = "I apologize, but I'm having trouble processing that right now. ";
      if (error.message?.includes('API key')) {
        errorText = "I'm Calin, but I need to be configured with an API key to respond. Please check your environment variables.";
      } else if (error.message?.includes('model')) {
        errorText = "There's an issue with the AI model. Please try again in a moment.";
      } else {
        errorText += "Could you please try rephrasing your question?";
      }
      
      const errorMessage = {
        id: messages.length + 2,
        text: errorText,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="chatbot-side-panel calin-chatbot">
      <div className="chatbot-container">
        <div className="chatbot-header">
          <div className="chatbot-title">
            <i className="fas fa-headset"></i>
            <span>Calin - AI Receptionist</span>
          </div>
          {onClose && (
            <button className="chatbot-close" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        <div className="chatbot-messages">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}
            >
              <div className="message-content">
                {message.text.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < message.text.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
              <div className="message-time">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message bot-message">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {quickReplies.length > 0 && messages.length <= 2 && (
          <div className="quick-replies">
            {quickReplies.map((reply, index) => (
              <button
                key={index}
                className="quick-reply-btn"
                onClick={() => handleQuickReply(reply)}
                disabled={isLoading}
              >
                {reply}
              </button>
            ))}
          </div>
        )}

        <div className="chatbot-input-container">
          <input
            ref={inputRef}
            type="text"
            className="chatbot-input"
            placeholder="Type your message..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <button
            className="chatbot-send-btn"
            onClick={sendMessage}
            disabled={!inputMessage.trim() || isLoading}
          >
            <i className="fas fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalinChatbot;

